import type { PermissionType } from "../schema";

export interface PermissionDefinitionEntry {
  key: string;
  module: string;
  type: PermissionType;
  label: string;
  description: string;
  sortOrder: number;
}

export const PERMISSION_REGISTRY: PermissionDefinitionEntry[] = [
  // ============================================================================
  // DASHBOARD MODULE
  // ============================================================================
  {
    key: "dashboard.module.visible",
    module: "dashboard",
    type: "module",
    label: "View Dashboard",
    description: "Access to the main dashboard",
    sortOrder: 100,
  },

  // ============================================================================
  // LEADS MODULE
  // ============================================================================
  {
    key: "leads.module.visible",
    module: "leads",
    type: "module",
    label: "View Leads Module",
    description: "Access to the Leads section in navigation",
    sortOrder: 200,
  },
  {
    key: "leads.feature.view",
    module: "leads",
    type: "feature",
    label: "View Leads",
    description: "View lead records",
    sortOrder: 210,
  },
  {
    key: "leads.feature.create",
    module: "leads",
    type: "feature",
    label: "Create Leads",
    description: "Create new lead records",
    sortOrder: 220,
  },
  {
    key: "leads.feature.edit",
    module: "leads",
    type: "feature",
    label: "Edit Leads",
    description: "Modify existing lead records",
    sortOrder: 230,
  },
  {
    key: "leads.feature.delete",
    module: "leads",
    type: "feature",
    label: "Delete Leads",
    description: "Delete lead records",
    sortOrder: 240,
  },
  {
    key: "leads.feature.import",
    module: "leads",
    type: "feature",
    label: "Import Leads",
    description: "Bulk import leads from CSV",
    sortOrder: 250,
  },
  {
    key: "leads.feature.export",
    module: "leads",
    type: "feature",
    label: "Export Leads",
    description: "Export leads to CSV",
    sortOrder: 260,
  },
  {
    key: "leads.feature.bulkActions",
    module: "leads",
    type: "feature",
    label: "Bulk Actions",
    description: "Show bulk actions button for leads",
    sortOrder: 270,
  },
  {
    key: "leads.qualification.override",
    module: "leads",
    type: "feature",
    label: "Override AI Qualification",
    description: "Manually change a lead's AI qualification rating, label, or summary",
    sortOrder: 280,
  },

  // ============================================================================
  // CLIENTS MODULE
  // ============================================================================
  {
    key: "clients.module.visible",
    module: "clients",
    type: "module",
    label: "View Clients Module",
    description: "Access to the Clients section in navigation",
    sortOrder: 300,
  },
  {
    key: "clients.feature.view",
    module: "clients",
    type: "feature",
    label: "View Clients",
    description: "View client records",
    sortOrder: 310,
  },
  {
    key: "clients.feature.create",
    module: "clients",
    type: "feature",
    label: "Create Clients",
    description: "Create new client records",
    sortOrder: 320,
  },
  {
    key: "clients.feature.edit",
    module: "clients",
    type: "feature",
    label: "Edit Clients",
    description: "Modify existing client records",
    sortOrder: 330,
  },
  {
    key: "clients.feature.delete",
    module: "clients",
    type: "feature",
    label: "Delete Clients",
    description: "Delete client records",
    sortOrder: 340,
  },
  {
    key: "clients.feature.import",
    module: "clients",
    type: "feature",
    label: "Import Clients",
    description: "Bulk import clients from CSV",
    sortOrder: 350,
  },
  {
    key: "clients.feature.export",
    module: "clients",
    type: "feature",
    label: "Export Clients",
    description: "Export clients to CSV",
    sortOrder: 360,
  },
  {
    key: "clients.feature.bulkActions",
    module: "clients",
    type: "feature",
    label: "Bulk Actions",
    description: "Show bulk actions button for clients",
    sortOrder: 370,
  },
  {
    key: "clients.feature.editRegion",
    module: "clients",
    type: "feature",
    label: "Edit Client Region",
    description: "Change the Region assigned to a client (in addition to general edit access)",
    sortOrder: 380,
  },

  // ============================================================================
  // INVOICES MODULE
  // ============================================================================
  {
    key: "invoices.module.visible",
    module: "invoices",
    type: "module",
    label: "View Invoices Module",
    description: "Access to the Invoices section in navigation",
    sortOrder: 400,
  },
  {
    key: "invoices.feature.view",
    module: "invoices",
    type: "feature",
    label: "View Invoices",
    description: "View invoice records",
    sortOrder: 410,
  },
  {
    key: "invoices.feature.create",
    module: "invoices",
    type: "feature",
    label: "Create Invoices",
    description: "Create new invoices",
    sortOrder: 420,
  },
  {
    key: "invoices.feature.edit",
    module: "invoices",
    type: "feature",
    label: "Edit Invoices",
    description: "Modify existing invoices",
    sortOrder: 430,
  },
  {
    key: "invoices.feature.delete",
    module: "invoices",
    type: "feature",
    label: "Delete Invoices",
    description: "Delete invoice records",
    sortOrder: 440,
  },
  {
    key: "invoices.feature.editDatePaid",
    module: "invoices",
    type: "feature",
    label: "Edit Date Paid (after marking paid)",
    description: "Edit the Date Paid value on an already-paid invoice",
    sortOrder: 445,
  },
  {
    key: "invoices.feature.send",
    module: "invoices",
    type: "feature",
    label: "Send Invoices",
    description: "Send invoices via email",
    sortOrder: 450,
  },
  {
    key: "invoices.feature.import",
    module: "invoices",
    type: "feature",
    label: "Import Invoices",
    description: "Bulk import invoices from CSV",
    sortOrder: 460,
  },
  {
    key: "invoices.feature.export",
    module: "invoices",
    type: "feature",
    label: "Export Invoices",
    description: "Export invoices to CSV",
    sortOrder: 470,
  },
  {
    key: "invoices.feature.bulkActions",
    module: "invoices",
    type: "feature",
    label: "Bulk Actions",
    description: "Show bulk actions button for invoices",
    sortOrder: 480,
  },

  // ============================================================================
  // BILLING MODULE
  // ============================================================================
  {
    key: "billing.module.visible",
    module: "billing",
    type: "module",
    label: "View Billing Module",
    description: "Access to the Billing Audit section in navigation",
    sortOrder: 500,
  },
  {
    key: "billing.feature.view",
    module: "billing",
    type: "feature",
    label: "View Billing Records",
    description: "View billing audit records",
    sortOrder: 510,
  },
  {
    key: "billing.feature.edit",
    module: "billing",
    type: "feature",
    label: "Edit Billing Records",
    description: "Modify billing audit records",
    sortOrder: 520,
  },
  {
    key: "billing.feature.viewPayables",
    module: "billing",
    type: "feature",
    label: "View Accounts Payable",
    description: "View accounts payable transactions",
    sortOrder: 530,
  },
  {
    key: "billing.feature.viewReceivables",
    module: "billing",
    type: "feature",
    label: "View Accounts Receivable",
    description: "View accounts receivable log",
    sortOrder: 540,
  },
  {
    key: "billing.feature.viewCommissions",
    module: "billing",
    type: "feature",
    label: "View Commissions",
    description: "View commission payouts and structures",
    sortOrder: 550,
  },
  {
    key: "billing.feature.logExpense",
    module: "billing",
    type: "feature",
    label: "Log Expenses",
    description: "Log new expenses and payable transactions",
    sortOrder: 560,
  },
  {
    key: "billing.feature.payViaPaypal",
    module: "billing",
    type: "feature",
    label: "Execute PayPal Payments",
    description: "Make PayPal payouts to contractors and talent",
    sortOrder: 570,
  },
  {
    key: "billing.feature.overrideCommission",
    module: "billing",
    type: "feature",
    label: "Override Commissions",
    description: "Override calculated commission amounts",
    sortOrder: 580,
  },
  {
    key: "billing.feature.billingAudit",
    module: "billing",
    type: "feature",
    label: "Billing Audit",
    description: "Access billing audit and reconciliation tools",
    sortOrder: 590,
  },

  // ============================================================================
  // SCHEDULE MODULE
  // ============================================================================
  {
    key: "schedule.module.visible",
    module: "schedule",
    type: "module",
    label: "View Schedule Module",
    description: "Access to the Master Schedule section",
    sortOrder: 600,
  },
  {
    key: "schedule.feature.view",
    module: "schedule",
    type: "feature",
    label: "View Schedule",
    description: "View master schedule entries",
    sortOrder: 610,
  },
  {
    key: "schedule.feature.edit",
    module: "schedule",
    type: "feature",
    label: "Edit Schedule",
    description: "Modify schedule entries",
    sortOrder: 620,
  },
  {
    key: "schedule.feature.tourPlanner",
    module: "schedule",
    type: "feature",
    label: "Tour Planner",
    description: "Plan multi-stop tours with route optimization and routing sheets",
    sortOrder: 630,
  },
  {
    // Phase 3 "Lite mode" gate. When false, the Book Show dialog hides
    // the "Advanced settings" link, which means the user can no longer
    // open the multi-date/tour matrix or toggle a booking to recurring.
    // The simple booking form is unaffected. Default ON for admin and
    // sales_rep, OFF for artist/billing/marketing — flip per user from
    // the Users & Permissions admin panel as needed.
    key: "booking.feature.advancedSettings",
    module: "schedule",
    type: "feature",
    label: "Show Advanced Booking Settings",
    description:
      "Show the 'Advanced settings' link in Book Show dialogs (multi-date / tour matrix and recurring booking).",
    sortOrder: 630,
  },

  // ============================================================================
  // APPROVALS MODULE (ACCESS REQUESTS)
  // ============================================================================
  {
    key: "approvals.module.visible",
    module: "approvals",
    type: "module",
    label: "View Approvals Module",
    description: "Access to the Approvals/Intake section in navigation",
    sortOrder: 700,
  },
  {
    key: "approvals.feature.viewRequests",
    module: "approvals",
    type: "feature",
    label: "View Event Requests",
    description: "View pending show requests",
    sortOrder: 710,
  },
  {
    key: "approvals.feature.approve",
    module: "approvals",
    type: "feature",
    label: "Approve/Deny Requests",
    description: "Approve or deny show requests",
    sortOrder: 720,
  },
  {
    key: "approvals.feature.convert",
    module: "approvals",
    type: "feature",
    label: "Convert to Lead/Client",
    description: "Convert approved requests to leads or clients",
    sortOrder: 730,
  },

  // ============================================================================
  // COMMUNICATIONS MODULE
  // ============================================================================
  {
    key: "communications.module.visible",
    module: "communications",
    type: "module",
    label: "View Communications Module",
    description: "Access to email templates, automations, and campaigns",
    sortOrder: 800,
  },
  {
    key: "communications.feature.viewTemplates",
    module: "communications",
    type: "feature",
    label: "View Email Templates",
    description: "View email templates",
    sortOrder: 810,
  },
  {
    key: "communications.feature.editTemplates",
    module: "communications",
    type: "feature",
    label: "Edit Email Templates",
    description: "Create and modify email templates",
    sortOrder: 820,
  },
  {
    key: "communications.feature.sendEmails",
    module: "communications",
    type: "feature",
    label: "Send Emails",
    description: "Send individual and bulk emails",
    sortOrder: 830,
  },
  {
    key: "communications.feature.viewAutomations",
    module: "communications",
    type: "feature",
    label: "View Email Automations",
    description: "View email automation rules",
    sortOrder: 840,
  },
  {
    key: "communications.feature.editAutomations",
    module: "communications",
    type: "feature",
    label: "Edit Email Automations",
    description: "Create and modify email automations",
    sortOrder: 850,
  },
  {
    key: "communications.feature.viewDripCampaigns",
    module: "communications",
    type: "feature",
    label: "View Drip Campaigns",
    description: "View drip email campaigns",
    sortOrder: 860,
  },
  {
    key: "communications.feature.editDripCampaigns",
    module: "communications",
    type: "feature",
    label: "Edit Drip Campaigns",
    description: "Create and modify drip campaigns",
    sortOrder: 870,
  },
  {
    key: "communications.feature.viewSmsCampaigns",
    module: "communications",
    type: "feature",
    label: "View SMS Campaigns",
    description: "View SMS campaigns",
    sortOrder: 880,
  },
  {
    key: "communications.feature.editSmsCampaigns",
    module: "communications",
    type: "feature",
    label: "Edit SMS Campaigns",
    description: "Create and modify SMS campaigns",
    sortOrder: 890,
  },
  {
    key: "communications.feature.sendSms",
    module: "communications",
    type: "feature",
    label: "Send SMS",
    description: "Send SMS messages",
    sortOrder: 895,
  },
  {
    key: "communications.feature.viewMasterDripCampaigns",
    module: "communications",
    type: "feature",
    label: "View Master Drip Campaigns",
    description: "View and access master drip campaigns created by admins",
    sortOrder: 896,
  },
  {
    key: "communications.feature.viewMasterTemplates",
    module: "communications",
    type: "feature",
    label: "View Master Templates",
    description: "View and access master email templates created by admins",
    sortOrder: 897,
  },
  {
    key: "communications.feature.coldEmail",
    module: "communications",
    type: "feature",
    label: "Cold Email Mode",
    description: "Route outreach through the high-volume cold relay",
    sortOrder: 898,
  },

  // ============================================================================
  // CALENDAR HUB MODULE
  // ============================================================================
  {
    key: "calendarHub.module.visible",
    module: "calendarHub",
    type: "module",
    label: "View Calendar Hub",
    description: "Access to the Calendar Hub dashboard and views",
    sortOrder: 850,
  },
  {
    key: "calendarHub.feature.viewTimeline",
    module: "calendarHub",
    type: "feature",
    label: "View Timeline",
    description: "View the timeline list view",
    sortOrder: 851,
  },
  {
    key: "calendarHub.feature.viewDashboard",
    module: "calendarHub",
    type: "feature",
    label: "View Dashboard",
    description: "View the KPI dashboard with goals and metrics",
    sortOrder: 852,
  },
  {
    key: "calendarHub.feature.viewGrid",
    module: "calendarHub",
    type: "feature",
    label: "View Calendar Grid",
    description: "View the traditional calendar grid",
    sortOrder: 853,
  },
  {
    key: "calendarHub.feature.viewOrgTotals",
    module: "calendarHub",
    type: "feature",
    label: "View Organization Totals",
    description: "View org-wide booking dollars and metrics (admin only)",
    sortOrder: 854,
  },
  {
    key: "calendarHub.feature.editTasks",
    module: "calendarHub",
    type: "feature",
    label: "Edit Tasks",
    description: "Create and manage calendar tasks",
    sortOrder: 855,
  },
  {
    key: "calendarHub.feature.editGoals",
    module: "calendarHub",
    type: "feature",
    label: "Edit Goals",
    description: "Set and manage sales goals and targets",
    sortOrder: 856,
  },
  {
    key: "calendarHub.feature.editFocus",
    module: "calendarHub",
    type: "feature",
    label: "Edit Sales Focus",
    description: "Assign region/client type/artist focus by date range",
    sortOrder: 857,
  },
  {
    key: "calendarHub.feature.editRegions",
    module: "calendarHub",
    type: "feature",
    label: "Edit Regions",
    description: "Create and modify regions",
    sortOrder: 858,
  },
  {
    // Calendar Hub Slice 1: gates the per-user "Time Blocks" tab where
    // a user creates Fixed/Fluid/Life blocks on their own calendar.
    // Anyone with the calendar hub visible can already see their blocks;
    // this perm gates create / edit / delete on the user's own blocks.
    key: "calendarHub.feature.editTimeBlocks",
    module: "calendarHub",
    type: "feature",
    label: "Edit Personal Time Blocks",
    description: "Create, edit, and delete your own Fixed/Fluid/Life time blocks",
    sortOrder: 859,
  },
  {
    key: "calendarHub.feature.coachMode",
    module: "calendarHub",
    type: "feature",
    label: "Coach Mode",
    description: "Per-user life-coach layer inside the assistant (opt-in)",
    sortOrder: 860,
  },

  // ============================================================================
  // LEAD GEN MODULE
  // ============================================================================
  {
    key: "leadGen.module.visible",
    module: "leadGen",
    type: "module",
    label: "View Lead Gen Module",
    description: "Access to the Lead Generation sandbox powered by Google Places",
    sortOrder: 860,
  },
  {
    key: "leadGen.feature.search",
    module: "leadGen",
    type: "feature",
    label: "Run Lead Gen Searches",
    description: "Start Google Places searches to populate the sandbox",
    sortOrder: 861,
  },
  {
    key: "leadGen.feature.import",
    module: "leadGen",
    type: "feature",
    label: "Import Leads from Sandbox",
    description: "Import sandbox items as leads into the CRM",
    sortOrder: 862,
  },
  {
    key: "leadGen.feature.clear",
    module: "leadGen",
    type: "feature",
    label: "Clear Sandbox",
    description: "Clear all items from the Lead Gen sandbox",
    sortOrder: 863,
  },
  {
    key: "leadGen.feature.aiSearch",
    module: "leadGen",
    type: "feature",
    label: "AI Search",
    description: "AI-powered prospect search in the Lead Gen module",
    sortOrder: 864,
  },

  // ============================================================================
  // SOCIAL MEDIA MODULE (under Marketing)
  // ============================================================================
  {
    key: "socialMedia.module.visible",
    module: "socialMedia",
    type: "module",
    label: "View Social Media Module",
    description: "Access to the Social Media management area under Marketing",
    sortOrder: 870,
  },
  {
    key: "socialMedia.feature.viewCalendar",
    module: "socialMedia",
    type: "feature",
    label: "View Calendar & Queue",
    description: "See scheduled and published social posts",
    sortOrder: 871,
  },
  {
    key: "socialMedia.feature.viewMediaLibrary",
    module: "socialMedia",
    type: "feature",
    label: "View Media Library",
    description: "Browse uploaded media assets",
    sortOrder: 872,
  },
  {
    key: "socialMedia.feature.uploadMedia",
    module: "socialMedia",
    type: "feature",
    label: "Upload Media",
    description: "Upload images/videos to the social media library",
    sortOrder: 873,
  },
  {
    key: "socialMedia.feature.deleteMedia",
    module: "socialMedia",
    type: "feature",
    label: "Delete Media",
    description: "Delete assets from the social media library",
    sortOrder: 874,
  },
  {
    key: "socialMedia.feature.createDraft",
    module: "socialMedia",
    type: "feature",
    label: "Create Drafts",
    description: "Create new draft social posts",
    sortOrder: 875,
  },
  {
    key: "socialMedia.feature.editDraft",
    module: "socialMedia",
    type: "feature",
    label: "Edit Drafts",
    description: "Edit captions, hashtags, thumbnails, and other draft fields",
    sortOrder: 876,
  },
  {
    key: "socialMedia.feature.deletePost",
    module: "socialMedia",
    type: "feature",
    label: "Delete Posts",
    description: "Delete draft or scheduled posts",
    sortOrder: 877,
  },
  {
    key: "socialMedia.feature.schedulePost",
    module: "socialMedia",
    type: "feature",
    label: "Schedule Posts",
    description: "Move posts to the scheduled state",
    sortOrder: 878,
  },
  {
    key: "socialMedia.feature.publishPost",
    module: "socialMedia",
    type: "feature",
    label: "Publish Posts",
    description: "Publish posts immediately to connected platforms",
    sortOrder: 879,
  },
  {
    key: "socialMedia.feature.approvePost",
    module: "socialMedia",
    type: "feature",
    label: "Approve Posts for Publishing",
    description: "Approve posts that require internal sign-off before going live",
    sortOrder: 880,
  },
  {
    key: "socialMedia.feature.viewAnalytics",
    module: "socialMedia",
    type: "feature",
    label: "View Analytics",
    description: "See per-post and per-account engagement metrics",
    sortOrder: 881,
  },
  {
    key: "socialMedia.feature.manageConnections",
    module: "socialMedia",
    type: "feature",
    label: "Manage Platform Connections",
    description: "Connect or disconnect social platform accounts (Meta, TikTok, YouTube, etc.)",
    sortOrder: 882,
  },
  {
    key: "socialMedia.feature.manageSettings",
    module: "socialMedia",
    type: "feature",
    label: "Manage Social Media Settings",
    description: "Edit storage mode, approval workflow, branding, and other module settings",
    sortOrder: 883,
  },
  {
    key: "socialMedia.feature.manageCollaborators",
    module: "socialMedia",
    type: "feature",
    label: "Invite & Manage Collaborators",
    description: "Invite outside collaborators (video editors, etc.) and manage their access",
    sortOrder: 884,
  },
  {
    key: "adminPanel.feature.socialMediaSettings",
    module: "adminPanel",
    type: "feature",
    label: "Social Media Settings (Admin Panel)",
    description: "Configure the workspace's Social Media module from the Admin Panel",
    sortOrder: 885,
  },

  // ============================================================================
  // REPORTS MODULE
  // ============================================================================
  {
    key: "reports.module.visible",
    module: "reports",
    type: "module",
    label: "View Reports Module",
    description: "Access to executive reports and analytics",
    sortOrder: 900,
  },
  {
    key: "reports.feature.viewRevenue",
    module: "reports",
    type: "feature",
    label: "View Revenue Reports",
    description: "View revenue and financial reports",
    sortOrder: 910,
  },
  {
    key: "reports.feature.viewBookings",
    module: "reports",
    type: "feature",
    label: "View Booking Reports",
    description: "View booking analytics",
    sortOrder: 920,
  },
  {
    key: "reports.feature.exportReports",
    module: "reports",
    type: "feature",
    label: "Export Reports",
    description: "Export reports to CSV/PDF",
    sortOrder: 930,
  },

  // ============================================================================
  // MY PROFILE MODULE (ARTIST)
  // ============================================================================
  {
    key: "myProfile.module.visible",
    module: "myProfile",
    type: "module",
    label: "View My Profile",
    description: "Access to personal talent profile",
    sortOrder: 1000,
  },
  {
    key: "myProfile.feature.edit",
    module: "myProfile",
    type: "feature",
    label: "Edit My Profile",
    description: "Edit personal talent profile",
    sortOrder: 1010,
  },

  // ============================================================================
  // MY SHOWS MODULE (ARTIST)
  // ============================================================================
  {
    key: "myEvents.module.visible",
    module: "myEvents",
    type: "module",
    label: "View My Events",
    description: "Access to personal show schedule",
    sortOrder: 1100,
  },
  {
    key: "myEvents.feature.view",
    module: "myEvents",
    type: "feature",
    label: "View Event Details",
    description: "View details of assigned shows",
    sortOrder: 1110,
  },

  // ============================================================================
  // TALENT MODULE (Task #419 — top-level Talent nav module)
  // ============================================================================
  {
    key: "talent.module.visible",
    module: "talent",
    type: "module",
    label: "View Talent Module",
    description: "Access to the Talent section (Callsheets, Contacts, Calendar)",
    sortOrder: 1150,
  },
  {
    key: "talent.feature.callsheets",
    module: "talent",
    type: "feature",
    label: "Call Sheets",
    description: "View and manage call sheets in the Talent module",
    sortOrder: 1160,
  },
  {
    key: "talent.feature.contacts",
    module: "talent",
    type: "feature",
    label: "Talent Contacts",
    description: "View and manage the talent address book",
    sortOrder: 1170,
  },
  {
    key: "talent.feature.calendar",
    module: "talent",
    type: "feature",
    label: "Talent Calendar",
    description: "View and manage the talent calendar hub",
    sortOrder: 1180,
  },

  // ============================================================================
  // ADMIN PANEL MODULE
  // ============================================================================
  {
    key: "adminPanel.module.visible",
    module: "adminPanel",
    type: "module",
    label: "View Admin Panel",
    description: "Access to the Administration section",
    sortOrder: 2000,
  },
  {
    key: "adminPanel.feature.manageUsers",
    module: "adminPanel",
    type: "feature",
    label: "Manage Users",
    description: "Add, edit, and manage user accounts",
    sortOrder: 2010,
  },
  {
    key: "adminPanel.feature.accessRequests",
    module: "adminPanel",
    type: "feature",
    label: "Access Requests",
    description: "Review and approve pending access requests",
    sortOrder: 2020,
  },
  {
    key: "adminPanel.feature.workflowSettings",
    module: "adminPanel",
    type: "feature",
    label: "Workflow Settings",
    description: "Configure approval workflows and settings",
    sortOrder: 2025,
  },
  {
    key: "adminPanel.feature.managePermissions",
    module: "adminPanel",
    type: "feature",
    label: "Manage Permissions",
    description: "Configure role and user permissions",
    sortOrder: 2030,
  },
  {
    key: "adminPanel.feature.manageCommissions",
    module: "adminPanel",
    type: "feature",
    label: "Manage Commissions",
    description: "Create and manage commission structures and rules",
    sortOrder: 2035,
  },
  {
    key: "adminPanel.feature.lateFeeSettings",
    module: "adminPanel",
    type: "feature",
    label: "Late Fee Settings",
    description: "Configure late fee tiers and exceptions",
    sortOrder: 2040,
  },
  {
    key: "adminPanel.feature.manageTalent",
    module: "adminPanel",
    type: "feature",
    label: "Manage Talent",
    description: "Configure talent profiles",
    sortOrder: 2050,
  },
  {
    key: "adminPanel.feature.manageClientTypes",
    module: "adminPanel",
    type: "feature",
    label: "Manage Client Types",
    description: "Configure client type categories",
    sortOrder: 2060,
  },
  {
    key: "adminPanel.feature.brandingSettings",
    module: "adminPanel",
    type: "feature",
    label: "Branding Settings",
    description: "Configure company logo and branding",
    sortOrder: 2070,
  },
  {
    key: "adminPanel.feature.paymentPortal",
    module: "adminPanel",
    type: "feature",
    label: "Payment Portal",
    description: "Connect Stripe/PayPal payment providers",
    sortOrder: 2080,
  },
  {
    key: "adminPanel.feature.invoiceSettings",
    module: "adminPanel",
    type: "feature",
    label: "Invoice Settings",
    description: "Configure invoice display and terms",
    sortOrder: 2090,
  },
  {
    key: "adminPanel.feature.profileSettings",
    module: "adminPanel",
    type: "feature",
    label: "Profile Settings",
    description: "Configure profile editing permissions",
    sortOrder: 2100,
  },
  {
    key: "adminPanel.feature.approvalSettings",
    module: "adminPanel",
    type: "feature",
    label: "Approval Settings",
    description: "Assign approval workflows by region/talent",
    sortOrder: 2110,
  },
  // Task #704 — Manager Panel module visibility. Special-cased in the
  // resolver: granted when `userProfile.isManager = true` OR the user is
  // an admin. Adding it to the registry so the navigation gate and the
  // permissions UI both see it.
  {
    key: "managerPanel.module.visible",
    module: "managerPanel",
    type: "module",
    label: "View Manager Panel",
    description: "Access the Manager Panel (auto-granted to managers and admins)",
    sortOrder: 2200,
  },
  {
    key: "adminPanel.feature.teamActivity",
    module: "adminPanel",
    type: "feature",
    label: "Team Activity",
    description: "View team activity log",
    sortOrder: 2120,
  },
  {
    key: "adminPanel.feature.communicationsSettings",
    module: "adminPanel",
    type: "feature",
    label: "Communications Settings",
    description: "Configure SMS providers and permissions",
    sortOrder: 2130,
  },
  {
    key: "adminPanel.feature.signatureBlock",
    module: "adminPanel",
    type: "feature",
    label: "Signature Block",
    description: "Configure signature block on invoices",
    sortOrder: 2135,
  },
  {
    key: "adminPanel.feature.workspaceIcon",
    module: "adminPanel",
    type: "feature",
    label: "Workspace Icon",
    description: "Upload and configure workspace icon/logo",
    sortOrder: 2136,
  },
  {
    key: "adminPanel.feature.talentIntakeForms",
    module: "adminPanel",
    type: "feature",
    label: "Talent Intake Forms",
    description: "Configure intake forms for talent",
    sortOrder: 2140,
  },
  {
    key: "adminPanel.feature.regionForms",
    module: "adminPanel",
    type: "feature",
    label: "Region Booking Forms",
    description: "Configure region-based booking forms",
    sortOrder: 2141,
  },
  {
    key: "adminPanel.feature.bulkOperations",
    module: "adminPanel",
    type: "feature",
    label: "Bulk Import/Export",
    description: "Access bulk import, export, and update functions",
    sortOrder: 2142,
  },
  {
    key: "adminPanel.feature.activityTracker",
    module: "adminPanel",
    type: "feature",
    label: "Activity Tracker",
    description: "View and manage activity tracking dashboard",
    sortOrder: 2143,
  },

  // ============================================================================
  // PROFILES MODULE (Talent Profiles)
  // ============================================================================
  {
    key: "profiles.module.visible",
    module: "profiles",
    type: "module",
    label: "View Profiles",
    description: "Access to the Profiles section in navigation",
    sortOrder: 2150,
  },
  {
    key: "profiles.feature.view",
    module: "profiles",
    type: "feature",
    label: "View Talent Profiles",
    description: "View talent profile pages",
    sortOrder: 2160,
  },

  // ============================================================================
  // PLATFORM (SUPER ADMIN) - Future use
  // ============================================================================
  {
    key: "platform.module.visible",
    module: "platform",
    type: "module",
    label: "Platform Administration",
    description: "Access to super admin platform controls",
    sortOrder: 9000,
  },
  {
    key: "platform.feature.managePlatformPermissions",
    module: "platform",
    type: "feature",
    label: "Manage Platform Permissions",
    description: "Apply platform-level permission overrides",
    sortOrder: 9010,
  },
  {
    key: "platform.feature.viewAllWorkspaces",
    module: "platform",
    type: "feature",
    label: "View All Workspaces",
    description: "View all workspaces on the platform",
    sortOrder: 9020,
  },
  {
    key: "platform.feature.manageWorkspaces",
    module: "platform",
    type: "feature",
    label: "Manage Workspaces",
    description: "Create and manage workspaces",
    sortOrder: 9030,
  },

  // ============================================================================
  // FORMS & CONTRACTS MODULE
  // ============================================================================
  {
    key: "forms.module.visible",
    module: "forms",
    type: "module",
    label: "View Documents Module",
    description: "Access to the Documents section in navigation",
    sortOrder: 350,
  },
  {
    key: "forms.feature.queueView",
    module: "forms",
    type: "feature",
    label: "View Document Queue",
    description: "View the queue of sent/completed documents",
    sortOrder: 360,
  },
  {
    key: "forms.feature.send",
    module: "forms",
    type: "feature",
    label: "Send Documents & Contracts",
    description: "Send documents and contracts to clients",
    sortOrder: 370,
  },
  {
    key: "forms.feature.uploadCompleted",
    module: "forms",
    type: "feature",
    label: "Upload Completed Documents",
    description: "Upload completed/signed documents",
    sortOrder: 380,
  },
  {
    key: "forms.feature.manageTemplates",
    module: "forms",
    type: "feature",
    label: "Manage Templates",
    description: "Create and manage document templates",
    sortOrder: 390,
  },
  {
    key: "forms.feature.manageDocusign",
    module: "forms",
    type: "feature",
    label: "Manage DocuSign",
    description: "Configure DocuSign integration",
    sortOrder: 400,
  },
  {
    key: "forms.feature.visualBuilder",
    module: "forms",
    type: "feature",
    label: "Visual Form Builder",
    description: "Create and edit document templates with the visual builder",
    sortOrder: 410,
  },

  // ============================================================================
  // MARKETING MODULE
  // ============================================================================
  {
    key: "marketing.module.visible",
    module: "marketing",
    type: "module",
    label: "View Marketing Module",
    description: "Access to the Marketing section in navigation",
    sortOrder: 1300,
  },
  {
    key: "marketing.feature.viewContacts",
    module: "marketing",
    type: "feature",
    label: "View Marketing Contacts",
    description: "View Media and Audience contact lists",
    sortOrder: 1310,
  },
  {
    key: "marketing.feature.editContacts",
    module: "marketing",
    type: "feature",
    label: "Edit Marketing Contacts",
    description: "Create, edit, and delete marketing contacts (media + audience)",
    sortOrder: 1320,
  },
  {
    key: "marketing.feature.importContacts",
    module: "marketing",
    type: "feature",
    label: "Import Marketing Contacts",
    description: "Bulk import marketing contacts from CSV",
    sortOrder: 1330,
  },
  {
    key: "marketing.feature.viewNewsletter",
    module: "marketing",
    type: "feature",
    label: "View Newsletter",
    description: "View newsletter issues and analytics",
    sortOrder: 1340,
  },
  {
    key: "marketing.feature.editNewsletter",
    module: "marketing",
    type: "feature",
    label: "Edit Newsletter",
    description: "Create, edit, and schedule newsletter issues",
    sortOrder: 1350,
  },
  {
    key: "marketing.feature.sendNewsletter",
    module: "marketing",
    type: "feature",
    label: "Send Newsletter",
    description: "Publish and send newsletter issues to recipients",
    sortOrder: 1360,
  },
  {
    key: "marketing.feature.viewDistribution",
    module: "marketing",
    type: "feature",
    label: "View Distribution Hub",
    description: "View distribution campaigns",
    sortOrder: 1370,
  },
  {
    key: "marketing.feature.launchDistribution",
    module: "marketing",
    type: "feature",
    label: "Launch Distribution Campaigns",
    description: "Create and launch multi-channel distribution campaigns",
    sortOrder: 1380,
  },
  // Task #705 — Ad Manager.
  {
    key: "marketing.feature.viewAds",
    module: "marketing",
    type: "feature",
    label: "View Ad Manager",
    description: "View ad accounts, campaigns, creatives, and attribution metrics",
    sortOrder: 1385,
  },
  {
    key: "marketing.feature.manageAds",
    module: "marketing",
    type: "feature",
    label: "Manage Ads",
    description: "Connect ad accounts, create/edit campaigns, generate creatives, manage tracking phone numbers",
    sortOrder: 1386,
  },
  {
    // Task #205 — Sponsor Hub.
    key: "marketing.feature.viewSponsorHub",
    module: "marketing",
    type: "feature",
    label: "View Sponsor Hub",
    description: "Access the Sponsor Hub module (leads, clients, and invoices for event sponsors)",
    sortOrder: 1387,
  },

  // ============================================================================
  // KNOWLEDGE CENTER MODULE
  // ============================================================================
  {
    key: "knowledge.module.visible",
    module: "knowledge",
    type: "module",
    label: "View Knowledge Center",
    description: "Access in-app help, training, and policy articles",
    sortOrder: 1400,
  },
  {
    key: "knowledge.feature.manage",
    module: "knowledge",
    type: "feature",
    label: "Manage Knowledge Articles",
    description: "Create, edit, publish, and delete knowledge articles",
    sortOrder: 1410,
  },
];

// Task #128 — DEFAULT_WORKSPACE_SLUG removed. It was set to the literal
// "warehouse-productions" and exported from this shared module, causing the
// dev-seed slug to appear in production code paths (permissions-bootstrap.ts,
// password-login response, etc.). Use the BOOTSTRAP_WORKSPACE_SLUG env var
// instead (server/permissions-bootstrap.ts). No runtime code should import a
// hardcoded tenant slug from the permissions registry.
export const DEFAULT_WORKSPACE_NAME = "New Workspace";

export type RolePermissionDefaults = Record<string, boolean>;

export const ROLE_PERMISSION_DEFAULTS: Record<string, RolePermissionDefaults> = {
  all: {
    "dashboard.module.visible": true,
    "schedule.feature.tourPlanner": true,
    "communications.feature.coldEmail": true,
    "calendarHub.feature.coachMode": true,
    "leadGen.feature.aiSearch": true,
    "forms.feature.visualBuilder": true,
    "leads.module.visible": true,
    "leads.feature.view": true,
    "leads.feature.create": true,
    "leads.feature.edit": true,
    "leads.feature.delete": false,
    "leads.feature.import": false,
    "leads.feature.export": true,
    "leads.feature.bulkActions": true,
    "leads.qualification.override": false,
    "clients.module.visible": true,
    "clients.feature.view": true,
    "clients.feature.create": true,
    "clients.feature.edit": true,
    "clients.feature.delete": false,
    "clients.feature.import": false,
    "clients.feature.export": true,
    "clients.feature.bulkActions": true,
    "clients.feature.editRegion": false,
    "invoices.module.visible": true,
    "invoices.feature.view": true,
    "invoices.feature.create": true,
    "invoices.feature.edit": true,
    "invoices.feature.delete": false,
    "invoices.feature.editDatePaid": false,
    "invoices.feature.send": true,
    "invoices.feature.import": false,
    "invoices.feature.export": true,
    "invoices.feature.bulkActions": true,
    "billing.module.visible": false,
    "billing.feature.view": false,
    "billing.feature.edit": false,
    "schedule.module.visible": true,
    "schedule.feature.view": true,
    "schedule.feature.edit": false,
    "booking.feature.advancedSettings": true,
    "approvals.module.visible": true,
    "approvals.feature.viewRequests": true,
    "approvals.feature.approve": false,
    "approvals.feature.convert": false,
    "communications.module.visible": true,
    "communications.feature.viewTemplates": true,
    "communications.feature.editTemplates": false,
    "communications.feature.sendEmails": true,
    "communications.feature.viewAutomations": true,
    "communications.feature.editAutomations": false,
    "communications.feature.viewDripCampaigns": true,
    "communications.feature.editDripCampaigns": false,
    "communications.feature.viewSmsCampaigns": true,
    "communications.feature.editSmsCampaigns": false,
    "communications.feature.sendSms": false,
    "communications.feature.viewMasterDripCampaigns": false,
    "communications.feature.viewMasterTemplates": false,
    "calendarHub.module.visible": true,
    "calendarHub.feature.viewTimeline": true,
    "calendarHub.feature.viewDashboard": true,
    "calendarHub.feature.viewGrid": true,
    "calendarHub.feature.viewOrgTotals": false,
    "calendarHub.feature.editTasks": false,
    "calendarHub.feature.editTimeBlocks": true,
    "calendarHub.feature.editGoals": false,
    "calendarHub.feature.editFocus": false,
    "calendarHub.feature.editRegions": false,
    "leadGen.module.visible": true,
    "leadGen.feature.search": true,
    "leadGen.feature.import": true,
    "leadGen.feature.clear": true,
    "reports.module.visible": true,
    "reports.feature.viewRevenue": true,
    "reports.feature.viewBookings": true,
    "reports.feature.exportReports": true,
    "myProfile.module.visible": false,
    "myProfile.feature.edit": false,
    "myEvents.module.visible": false,
    "myEvents.feature.view": false,
    "talent.module.visible": false,
    "talent.feature.callsheets": false,
    "talent.feature.contacts": false,
    "talent.feature.calendar": false,
    "adminPanel.module.visible": false,
    "adminPanel.feature.manageUsers": false,
    "adminPanel.feature.accessRequests": false,
    "adminPanel.feature.workflowSettings": false,
    "adminPanel.feature.managePermissions": false,
    "adminPanel.feature.manageCommissions": false,
    "adminPanel.feature.lateFeeSettings": false,
    "adminPanel.feature.manageTalent": false,
    "adminPanel.feature.manageClientTypes": false,
    "adminPanel.feature.brandingSettings": false,
    "adminPanel.feature.paymentPortal": false,
    "adminPanel.feature.invoiceSettings": false,
    "adminPanel.feature.profileSettings": false,
    "adminPanel.feature.approvalSettings": false,
    "adminPanel.feature.teamActivity": false,
    "adminPanel.feature.communicationsSettings": false,
    "adminPanel.feature.signatureBlock": false,
    "adminPanel.feature.workspaceIcon": false,
    "adminPanel.feature.talentIntakeForms": false,
    "adminPanel.feature.regionForms": false,
    "adminPanel.feature.bulkOperations": false,
    "adminPanel.feature.activityTracker": false,
    "billing.feature.billingAudit": false,
    "forms.module.visible": true,
    "forms.feature.queueView": true,
    "forms.feature.send": true,
    "forms.feature.uploadCompleted": true,
    "forms.feature.manageTemplates": false,
    "forms.feature.manageDocusign": false,
    "profiles.module.visible": false,
    "profiles.feature.view": false,
    "platform.module.visible": false,
    "platform.feature.managePlatformPermissions": false,
    "platform.feature.viewAllWorkspaces": false,
    "marketing.module.visible": true,
      "marketing.feature.viewContacts": true,
      "marketing.feature.editContacts": true,
      "marketing.feature.importContacts": true,
      "marketing.feature.viewNewsletter": true,
      "marketing.feature.editNewsletter": true,
      "marketing.feature.sendNewsletter": true,
      "marketing.feature.viewDistribution": true,
      "marketing.feature.launchDistribution": true,
      "marketing.feature.viewAds": true,
      "marketing.feature.manageAds": false,
      "marketing.feature.viewSponsorHub": true,
    "socialMedia.module.visible": false,
    "socialMedia.feature.viewCalendar": false,
    "socialMedia.feature.viewMediaLibrary": false,
    "socialMedia.feature.uploadMedia": false,
    "socialMedia.feature.deleteMedia": false,
    "socialMedia.feature.createDraft": false,
    "socialMedia.feature.editDraft": false,
    "socialMedia.feature.deletePost": false,
    "socialMedia.feature.schedulePost": false,
    "socialMedia.feature.publishPost": false,
    "socialMedia.feature.approvePost": false,
    "socialMedia.feature.viewAnalytics": false,
    "socialMedia.feature.manageConnections": false,
    "socialMedia.feature.manageSettings": false,
    "socialMedia.feature.manageCollaborators": false,
    "adminPanel.feature.socialMediaSettings": false,
    "knowledge.module.visible": true,
    "knowledge.feature.manage": true,
    "platform.feature.manageWorkspaces": false,
  },
  admin: {
    "dashboard.module.visible": true,
    "schedule.feature.tourPlanner": true,
    "communications.feature.coldEmail": true,
    "calendarHub.feature.coachMode": true,
    "leadGen.feature.aiSearch": true,
    "forms.feature.visualBuilder": true,
    "marketing.module.visible": true,
    "marketing.feature.viewSponsorHub": true,
    "leads.module.visible": true,
    "leads.feature.view": true,
    "leads.feature.create": true,
    "leads.feature.edit": true,
    "leads.feature.delete": true,
    "leads.feature.import": true,
    "leads.feature.export": true,
    "leads.feature.bulkActions": true,
    "leads.qualification.override": true,
    "clients.module.visible": true,
    "clients.feature.view": true,
    "clients.feature.create": true,
    "clients.feature.edit": true,
    "clients.feature.delete": true,
    "clients.feature.import": true,
    "clients.feature.export": true,
    "clients.feature.bulkActions": true,
    "clients.feature.editRegion": true,
    "invoices.module.visible": true,
    "invoices.feature.view": true,
    "invoices.feature.create": true,
    "invoices.feature.edit": true,
    "invoices.feature.delete": true,
    "invoices.feature.editDatePaid": true,
    "invoices.feature.send": true,
    "invoices.feature.import": true,
    "invoices.feature.export": true,
    "invoices.feature.bulkActions": true,
    "billing.module.visible": true,
    "billing.feature.view": true,
    "billing.feature.edit": true,
    "billing.feature.viewPayables": true,
    "billing.feature.viewReceivables": true,
    "billing.feature.viewCommissions": true,
    "billing.feature.logExpense": true,
    "billing.feature.payViaPaypal": true,
    "billing.feature.overrideCommission": true,
    "schedule.module.visible": true,
    "schedule.feature.view": true,
    "schedule.feature.edit": true,
    "booking.feature.advancedSettings": true,
    "approvals.module.visible": true,
    "approvals.feature.viewRequests": true,
    "approvals.feature.approve": true,
    "approvals.feature.convert": true,
    "communications.module.visible": true,
    "communications.feature.viewTemplates": true,
    "communications.feature.editTemplates": true,
    "communications.feature.sendEmails": true,
    "communications.feature.viewAutomations": true,
    "communications.feature.editAutomations": true,
    "communications.feature.viewDripCampaigns": true,
    "communications.feature.editDripCampaigns": true,
    "communications.feature.viewSmsCampaigns": true,
    "communications.feature.editSmsCampaigns": true,
    "communications.feature.sendSms": true,
    "communications.feature.viewMasterDripCampaigns": true,
    "communications.feature.viewMasterTemplates": true,
    "calendarHub.module.visible": true,
    "calendarHub.feature.viewTimeline": true,
    "calendarHub.feature.viewDashboard": true,
    "calendarHub.feature.viewGrid": true,
    "calendarHub.feature.viewOrgTotals": true,
    "calendarHub.feature.editTasks": true,
    "calendarHub.feature.editTimeBlocks": true,
    "calendarHub.feature.editGoals": true,
    "calendarHub.feature.editFocus": true,
    "calendarHub.feature.editRegions": true,
    "leadGen.module.visible": true,
    "leadGen.feature.search": true,
    "leadGen.feature.import": true,
    "leadGen.feature.clear": true,
    "reports.module.visible": true,
    "reports.feature.viewRevenue": true,
    "reports.feature.viewBookings": true,
    "reports.feature.exportReports": true,
    "myProfile.module.visible": false,
    "myProfile.feature.edit": false,
    "myEvents.module.visible": false,
    "myEvents.feature.view": false,
    "talent.module.visible": true,
    "talent.feature.callsheets": true,
    "talent.feature.contacts": true,
    "talent.feature.calendar": true,
    "adminPanel.module.visible": true,
    "adminPanel.feature.manageUsers": true,
    "adminPanel.feature.accessRequests": true,
    "adminPanel.feature.workflowSettings": true,
    "adminPanel.feature.managePermissions": true,
    "adminPanel.feature.manageCommissions": true,
    "adminPanel.feature.lateFeeSettings": true,
    "adminPanel.feature.manageTalent": true,
    "adminPanel.feature.manageClientTypes": true,
    "adminPanel.feature.brandingSettings": true,
    "adminPanel.feature.paymentPortal": true,
    "adminPanel.feature.invoiceSettings": true,
    "adminPanel.feature.profileSettings": true,
    "adminPanel.feature.approvalSettings": true,
    "adminPanel.feature.teamActivity": true,
    "adminPanel.feature.communicationsSettings": true,
    "adminPanel.feature.signatureBlock": true,
    "adminPanel.feature.workspaceIcon": true,
    "adminPanel.feature.talentIntakeForms": true,
    "adminPanel.feature.regionForms": true,
    "adminPanel.feature.bulkOperations": true,
    "adminPanel.feature.activityTracker": true,
    "billing.feature.billingAudit": true,
    "forms.module.visible": true,
    "forms.feature.queueView": true,
    "forms.feature.send": true,
    "forms.feature.uploadCompleted": true,
    "forms.feature.manageTemplates": true,
    "forms.feature.manageDocusign": true,
    "profiles.module.visible": true,
    "profiles.feature.view": true,
    "platform.module.visible": false,
    "platform.feature.managePlatformPermissions": false,
    "platform.feature.viewAllWorkspaces": false,
      "marketing.feature.viewContacts": true,
      "marketing.feature.editContacts": true,
      "marketing.feature.importContacts": true,
      "marketing.feature.viewNewsletter": true,
      "marketing.feature.editNewsletter": true,
      "marketing.feature.sendNewsletter": true,
      "marketing.feature.viewDistribution": true,
      "marketing.feature.launchDistribution": true,
      "marketing.feature.viewAds": true,
      "marketing.feature.manageAds": true,
    "socialMedia.module.visible": true,
    "socialMedia.feature.viewCalendar": true,
    "socialMedia.feature.viewMediaLibrary": true,
    "socialMedia.feature.uploadMedia": true,
    "socialMedia.feature.deleteMedia": true,
    "socialMedia.feature.createDraft": true,
    "socialMedia.feature.editDraft": true,
    "socialMedia.feature.deletePost": true,
    "socialMedia.feature.schedulePost": true,
    "socialMedia.feature.publishPost": true,
    "socialMedia.feature.approvePost": true,
    "socialMedia.feature.viewAnalytics": true,
    "socialMedia.feature.manageConnections": true,
    "socialMedia.feature.manageSettings": true,
    "socialMedia.feature.manageCollaborators": true,
    "adminPanel.feature.socialMediaSettings": true,
    "knowledge.module.visible": true,
    "knowledge.feature.manage": true,
    "platform.feature.manageWorkspaces": false,
  },
  sales_rep: {
    "dashboard.module.visible": true,
    "schedule.feature.tourPlanner": true,
    "communications.feature.coldEmail": true,
    "calendarHub.feature.coachMode": true,
    "leadGen.feature.aiSearch": true,
    "forms.feature.visualBuilder": true,
    "leads.module.visible": true,
    "leads.feature.view": true,
    "leads.feature.create": true,
    "leads.feature.edit": true,
    "leads.feature.delete": false,
    "leads.feature.import": false,
    "leads.feature.export": true,
    "leads.feature.bulkActions": true,
    "leads.qualification.override": false,
    "clients.module.visible": true,
    "clients.feature.view": true,
    "clients.feature.create": true,
    "clients.feature.edit": true,
    "clients.feature.delete": false,
    "clients.feature.import": false,
    "clients.feature.export": true,
    "clients.feature.bulkActions": true,
    "clients.feature.editRegion": false,
    "invoices.module.visible": true,
    "invoices.feature.view": true,
    "invoices.feature.create": true,
    "invoices.feature.edit": true,
    "invoices.feature.delete": false,
    "invoices.feature.editDatePaid": false,
    "invoices.feature.send": true,
    "invoices.feature.import": false,
    "invoices.feature.export": true,
    "invoices.feature.bulkActions": true,
    "billing.module.visible": false,
    "billing.feature.view": false,
    "billing.feature.edit": false,
    "billing.feature.viewPayables": false,
    "billing.feature.viewReceivables": false,
    "billing.feature.viewCommissions": false,
    "billing.feature.logExpense": false,
    "billing.feature.payViaPaypal": false,
    "billing.feature.overrideCommission": false,
    "schedule.module.visible": true,
    "schedule.feature.view": true,
    "schedule.feature.edit": false,
    "booking.feature.advancedSettings": true,
    "approvals.module.visible": true,
    "approvals.feature.viewRequests": true,
    "approvals.feature.approve": false,
    "approvals.feature.convert": true,
    "communications.module.visible": true,
    "communications.feature.viewTemplates": true,
    "communications.feature.editTemplates": false,
    "communications.feature.sendEmails": true,
    "communications.feature.viewAutomations": true,
    "communications.feature.editAutomations": false,
    "communications.feature.viewDripCampaigns": true,
    "communications.feature.editDripCampaigns": false,
    "communications.feature.viewSmsCampaigns": true,
    "communications.feature.editSmsCampaigns": false,
    "communications.feature.sendSms": false,
    "communications.feature.viewMasterDripCampaigns": false,
    "communications.feature.viewMasterTemplates": false,
    "calendarHub.module.visible": true,
    "calendarHub.feature.viewTimeline": true,
    "calendarHub.feature.viewDashboard": true,
    "calendarHub.feature.viewGrid": true,
    "calendarHub.feature.viewOrgTotals": false,
    "calendarHub.feature.editTasks": true,
    "calendarHub.feature.editTimeBlocks": true,
    "calendarHub.feature.editGoals": false,
    "calendarHub.feature.editFocus": false,
    "calendarHub.feature.editRegions": false,
    "leadGen.module.visible": true,
    "leadGen.feature.search": true,
    "leadGen.feature.import": true,
    "leadGen.feature.clear": true,
    "reports.module.visible": true,
    "reports.feature.viewRevenue": true,
    "reports.feature.viewBookings": true,
    "reports.feature.exportReports": true,
    "myProfile.module.visible": false,
    "myProfile.feature.edit": false,
    "myEvents.module.visible": false,
    "myEvents.feature.view": false,
    "adminPanel.module.visible": false,
    "adminPanel.feature.manageUsers": false,
    "adminPanel.feature.accessRequests": false,
    "adminPanel.feature.workflowSettings": false,
    "adminPanel.feature.managePermissions": false,
    "adminPanel.feature.manageCommissions": false,
    "adminPanel.feature.lateFeeSettings": false,
    "adminPanel.feature.manageTalent": false,
    "adminPanel.feature.manageClientTypes": false,
    "adminPanel.feature.brandingSettings": false,
    "adminPanel.feature.paymentPortal": false,
    "adminPanel.feature.invoiceSettings": false,
    "adminPanel.feature.profileSettings": false,
    "adminPanel.feature.approvalSettings": false,
    "adminPanel.feature.teamActivity": false,
    "adminPanel.feature.communicationsSettings": false,
    "adminPanel.feature.signatureBlock": false,
    "adminPanel.feature.workspaceIcon": false,
    "adminPanel.feature.talentIntakeForms": false,
    "adminPanel.feature.regionForms": false,
    "adminPanel.feature.bulkOperations": false,
    "adminPanel.feature.activityTracker": false,
    "billing.feature.billingAudit": false,
    "forms.module.visible": true,
    "forms.feature.queueView": true,
    "forms.feature.send": true,
    "forms.feature.uploadCompleted": true,
    "forms.feature.manageTemplates": false,
    "forms.feature.manageDocusign": false,
    "profiles.module.visible": false,
    "profiles.feature.view": false,
    "platform.module.visible": false,
    "platform.feature.managePlatformPermissions": false,
    "platform.feature.viewAllWorkspaces": false,
    "marketing.module.visible": false,
      "marketing.feature.viewContacts": false,
      "marketing.feature.editContacts": false,
      "marketing.feature.importContacts": false,
      "marketing.feature.viewNewsletter": false,
      "marketing.feature.editNewsletter": false,
      "marketing.feature.sendNewsletter": false,
      "marketing.feature.viewDistribution": false,
      "marketing.feature.launchDistribution": false,
    "socialMedia.module.visible": false,
    "socialMedia.feature.viewCalendar": false,
    "socialMedia.feature.viewMediaLibrary": false,
    "socialMedia.feature.uploadMedia": false,
    "socialMedia.feature.deleteMedia": false,
    "socialMedia.feature.createDraft": false,
    "socialMedia.feature.editDraft": false,
    "socialMedia.feature.deletePost": false,
    "socialMedia.feature.schedulePost": false,
    "socialMedia.feature.publishPost": false,
    "socialMedia.feature.approvePost": false,
    "socialMedia.feature.viewAnalytics": false,
    "socialMedia.feature.manageConnections": false,
    "socialMedia.feature.manageSettings": false,
    "socialMedia.feature.manageCollaborators": false,
    "adminPanel.feature.socialMediaSettings": false,
    "knowledge.module.visible": true,
    "knowledge.feature.manage": false,
    "platform.feature.manageWorkspaces": false,
  },
  artist: {
    "dashboard.module.visible": true,
    "schedule.feature.tourPlanner": true,
    "communications.feature.coldEmail": true,
    "calendarHub.feature.coachMode": true,
    "leadGen.feature.aiSearch": true,
    "forms.feature.visualBuilder": true,
    "leads.module.visible": false,
    "leads.feature.view": false,
    "leads.feature.create": false,
    "leads.feature.edit": false,
    "leads.feature.delete": false,
    "leads.feature.import": false,
    "leads.feature.export": false,
    "leads.feature.bulkActions": false,
    "leads.qualification.override": false,
    "clients.module.visible": false,
    "clients.feature.view": false,
    "clients.feature.create": false,
    "clients.feature.edit": false,
    "clients.feature.delete": false,
    "clients.feature.import": false,
    "clients.feature.export": false,
    "clients.feature.bulkActions": false,
    "clients.feature.editRegion": false,
    "invoices.module.visible": false,
    "invoices.feature.view": false,
    "invoices.feature.create": false,
    "invoices.feature.edit": false,
    "invoices.feature.delete": false,
    "invoices.feature.editDatePaid": false,
    "invoices.feature.send": false,
    "invoices.feature.import": false,
    "invoices.feature.export": false,
    "invoices.feature.bulkActions": false,
    "billing.module.visible": false,
    "billing.feature.view": false,
    "billing.feature.edit": false,
    "billing.feature.viewPayables": false,
    "billing.feature.viewReceivables": false,
    "billing.feature.viewCommissions": false,
    "billing.feature.logExpense": false,
    "billing.feature.payViaPaypal": false,
    "billing.feature.overrideCommission": false,
    "schedule.module.visible": true,
    "schedule.feature.view": true,
    "schedule.feature.edit": false,
    "booking.feature.advancedSettings": false,
    "approvals.module.visible": false,
    "approvals.feature.viewRequests": false,
    "approvals.feature.approve": false,
    "approvals.feature.convert": false,
    "communications.module.visible": false,
    "communications.feature.viewTemplates": false,
    "communications.feature.editTemplates": false,
    "communications.feature.sendEmails": false,
    "communications.feature.viewAutomations": false,
    "communications.feature.editAutomations": false,
    "communications.feature.viewDripCampaigns": false,
    "communications.feature.editDripCampaigns": false,
    "communications.feature.viewSmsCampaigns": false,
    "communications.feature.editSmsCampaigns": false,
    "communications.feature.sendSms": false,
    "communications.feature.viewMasterDripCampaigns": false,
    "communications.feature.viewMasterTemplates": false,
    "calendarHub.module.visible": true,
    "calendarHub.feature.viewTimeline": true,
    "calendarHub.feature.viewDashboard": true,
    "calendarHub.feature.viewGrid": true,
    "calendarHub.feature.viewOrgTotals": false,
    "calendarHub.feature.editTasks": false,
    "calendarHub.feature.editTimeBlocks": true,
    "calendarHub.feature.editGoals": false,
    "calendarHub.feature.editFocus": false,
    "calendarHub.feature.editRegions": false,
    "leadGen.module.visible": false,
    "leadGen.feature.search": false,
    "leadGen.feature.import": false,
    "leadGen.feature.clear": false,
    "reports.module.visible": false,
    "reports.feature.viewRevenue": false,
    "reports.feature.viewBookings": false,
    "reports.feature.exportReports": false,
    "myProfile.module.visible": true,
    "myProfile.feature.edit": true,
    "myEvents.module.visible": true,
    "myEvents.feature.view": true,
    "adminPanel.module.visible": false,
    "adminPanel.feature.manageUsers": false,
    "adminPanel.feature.accessRequests": false,
    "adminPanel.feature.workflowSettings": false,
    "adminPanel.feature.managePermissions": false,
    "adminPanel.feature.manageCommissions": false,
    "adminPanel.feature.lateFeeSettings": false,
    "adminPanel.feature.manageTalent": false,
    "adminPanel.feature.manageClientTypes": false,
    "adminPanel.feature.brandingSettings": false,
    "adminPanel.feature.paymentPortal": false,
    "adminPanel.feature.invoiceSettings": false,
    "adminPanel.feature.profileSettings": false,
    "adminPanel.feature.approvalSettings": false,
    "adminPanel.feature.teamActivity": false,
    "adminPanel.feature.communicationsSettings": false,
    "adminPanel.feature.signatureBlock": false,
    "adminPanel.feature.workspaceIcon": false,
    "adminPanel.feature.talentIntakeForms": false,
    "adminPanel.feature.regionForms": false,
    "adminPanel.feature.bulkOperations": false,
    "adminPanel.feature.activityTracker": false,
    "billing.feature.billingAudit": false,
    "forms.module.visible": false,
    "forms.feature.queueView": false,
    "forms.feature.send": false,
    "forms.feature.uploadCompleted": false,
    "forms.feature.manageTemplates": false,
    "forms.feature.manageDocusign": false,
    "profiles.module.visible": false,
    "profiles.feature.view": false,
    "platform.module.visible": false,
    "platform.feature.managePlatformPermissions": false,
    "platform.feature.viewAllWorkspaces": false,
    "marketing.module.visible": false,
      "marketing.feature.viewContacts": false,
      "marketing.feature.editContacts": false,
      "marketing.feature.importContacts": false,
      "marketing.feature.viewNewsletter": false,
      "marketing.feature.editNewsletter": false,
      "marketing.feature.sendNewsletter": false,
      "marketing.feature.viewDistribution": false,
      "marketing.feature.launchDistribution": false,
    "socialMedia.module.visible": false,
    "socialMedia.feature.viewCalendar": false,
    "socialMedia.feature.viewMediaLibrary": false,
    "socialMedia.feature.uploadMedia": false,
    "socialMedia.feature.deleteMedia": false,
    "socialMedia.feature.createDraft": false,
    "socialMedia.feature.editDraft": false,
    "socialMedia.feature.deletePost": false,
    "socialMedia.feature.schedulePost": false,
    "socialMedia.feature.publishPost": false,
    "socialMedia.feature.approvePost": false,
    "socialMedia.feature.viewAnalytics": false,
    "socialMedia.feature.manageConnections": false,
    "socialMedia.feature.manageSettings": false,
    "socialMedia.feature.manageCollaborators": false,
    "adminPanel.feature.socialMediaSettings": false,
    "knowledge.module.visible": true,
    "knowledge.feature.manage": false,
    "platform.feature.manageWorkspaces": false,
  },
  billing: {
    "dashboard.module.visible": true,
    "schedule.feature.tourPlanner": true,
    "communications.feature.coldEmail": true,
    "calendarHub.feature.coachMode": true,
    "leadGen.feature.aiSearch": true,
    "forms.feature.visualBuilder": true,
    "leads.module.visible": false,
    "leads.feature.view": false,
    "leads.feature.create": false,
    "leads.feature.edit": false,
    "leads.feature.delete": false,
    "leads.feature.import": false,
    "leads.feature.export": false,
    "leads.feature.bulkActions": false,
    "leads.qualification.override": false,
    "clients.module.visible": true,
    "clients.feature.view": true,
    "clients.feature.create": false,
    "clients.feature.edit": true,
    "clients.feature.delete": false,
    "clients.feature.import": false,
    "clients.feature.export": true,
    "clients.feature.bulkActions": true,
    "clients.feature.editRegion": false,
    "invoices.module.visible": true,
    "invoices.feature.view": true,
    "invoices.feature.create": true,
    "invoices.feature.edit": true,
    "invoices.feature.delete": false,
    "invoices.feature.editDatePaid": true,
    "invoices.feature.send": true,
    "invoices.feature.import": false,
    "invoices.feature.export": true,
    "invoices.feature.bulkActions": true,
    "billing.module.visible": true,
    "billing.feature.view": true,
    "billing.feature.edit": true,
    "billing.feature.viewPayables": true,
    "billing.feature.viewReceivables": true,
    "billing.feature.viewCommissions": true,
    "billing.feature.logExpense": true,
    "billing.feature.payViaPaypal": true,
    "billing.feature.overrideCommission": true,
    "schedule.module.visible": false,
    "schedule.feature.view": false,
    "schedule.feature.edit": false,
    "booking.feature.advancedSettings": false,
    "approvals.module.visible": false,
    "approvals.feature.viewRequests": false,
    "approvals.feature.approve": false,
    "approvals.feature.convert": false,
    "communications.module.visible": true,
    "communications.feature.viewTemplates": true,
    "communications.feature.editTemplates": false,
    "communications.feature.sendEmails": true,
    "communications.feature.viewAutomations": true,
    "communications.feature.editAutomations": false,
    "communications.feature.viewDripCampaigns": true,
    "communications.feature.editDripCampaigns": false,
    "communications.feature.viewSmsCampaigns": true,
    "communications.feature.editSmsCampaigns": false,
    "communications.feature.sendSms": false,
    "communications.feature.viewMasterDripCampaigns": false,
    "communications.feature.viewMasterTemplates": false,
    "calendarHub.module.visible": false,
    "calendarHub.feature.viewTimeline": false,
    "calendarHub.feature.viewDashboard": false,
    "calendarHub.feature.viewGrid": false,
    "calendarHub.feature.viewOrgTotals": false,
    "calendarHub.feature.editTasks": false,
    "calendarHub.feature.editTimeBlocks": true,
    "calendarHub.feature.editGoals": false,
    "calendarHub.feature.editFocus": false,
    "calendarHub.feature.editRegions": false,
    "leadGen.module.visible": false,
    "leadGen.feature.search": false,
    "leadGen.feature.import": false,
    "leadGen.feature.clear": false,
    "reports.module.visible": true,
    "reports.feature.viewRevenue": true,
    "reports.feature.viewBookings": true,
    "reports.feature.exportReports": true,
    "myProfile.module.visible": false,
    "myProfile.feature.edit": false,
    "myEvents.module.visible": false,
    "myEvents.feature.view": false,
    "adminPanel.module.visible": false,
    "adminPanel.feature.manageUsers": false,
    "adminPanel.feature.accessRequests": false,
    "adminPanel.feature.workflowSettings": false,
    "adminPanel.feature.managePermissions": false,
    "adminPanel.feature.manageCommissions": false,
    "adminPanel.feature.lateFeeSettings": false,
    "adminPanel.feature.manageTalent": false,
    "adminPanel.feature.manageClientTypes": false,
    "adminPanel.feature.brandingSettings": false,
    "adminPanel.feature.paymentPortal": false,
    "adminPanel.feature.invoiceSettings": false,
    "adminPanel.feature.profileSettings": false,
    "adminPanel.feature.approvalSettings": false,
    "adminPanel.feature.teamActivity": false,
    "adminPanel.feature.communicationsSettings": false,
    "adminPanel.feature.signatureBlock": false,
    "adminPanel.feature.workspaceIcon": false,
    "adminPanel.feature.talentIntakeForms": false,
    "adminPanel.feature.regionForms": false,
    "adminPanel.feature.bulkOperations": false,
    "adminPanel.feature.activityTracker": false,
    "billing.feature.billingAudit": true,
    "forms.module.visible": false,
    "forms.feature.queueView": false,
    "forms.feature.send": false,
    "forms.feature.uploadCompleted": false,
    "forms.feature.manageTemplates": false,
    "forms.feature.manageDocusign": false,
    "profiles.module.visible": false,
    "profiles.feature.view": false,
    "platform.module.visible": false,
    "platform.feature.managePlatformPermissions": false,
    "platform.feature.viewAllWorkspaces": false,
    "marketing.module.visible": false,
      "marketing.feature.viewContacts": false,
      "marketing.feature.editContacts": false,
      "marketing.feature.importContacts": false,
      "marketing.feature.viewNewsletter": false,
      "marketing.feature.editNewsletter": false,
      "marketing.feature.sendNewsletter": false,
      "marketing.feature.viewDistribution": false,
      "marketing.feature.launchDistribution": false,
    "socialMedia.module.visible": false,
    "socialMedia.feature.viewCalendar": false,
    "socialMedia.feature.viewMediaLibrary": false,
    "socialMedia.feature.uploadMedia": false,
    "socialMedia.feature.deleteMedia": false,
    "socialMedia.feature.createDraft": false,
    "socialMedia.feature.editDraft": false,
    "socialMedia.feature.deletePost": false,
    "socialMedia.feature.schedulePost": false,
    "socialMedia.feature.publishPost": false,
    "socialMedia.feature.approvePost": false,
    "socialMedia.feature.viewAnalytics": false,
    "socialMedia.feature.manageConnections": false,
    "socialMedia.feature.manageSettings": false,
    "socialMedia.feature.manageCollaborators": false,
    "adminPanel.feature.socialMediaSettings": false,
    "knowledge.module.visible": true,
    "knowledge.feature.manage": false,
    "platform.feature.manageWorkspaces": false,
  },
  marketing: {
    "dashboard.module.visible": true,
    "schedule.feature.tourPlanner": true,
    "communications.feature.coldEmail": true,
    "calendarHub.feature.coachMode": true,
    "leadGen.feature.aiSearch": true,
    "forms.feature.visualBuilder": true,
    "leads.module.visible": true,
    "leads.feature.view": true,
    "leads.feature.create": true,
    "leads.feature.edit": true,
    "leads.feature.delete": false,
    "leads.feature.import": true,
    "leads.feature.export": true,
    "leads.feature.bulkActions": true,
    "leads.qualification.override": false,
    "clients.module.visible": true,
    "clients.feature.view": true,
    "clients.feature.create": false,
    "clients.feature.edit": false,
    "clients.feature.delete": false,
    "clients.feature.import": false,
    "clients.feature.export": true,
    "clients.feature.bulkActions": false,
    "clients.feature.editRegion": false,
    "invoices.module.visible": false,
    "invoices.feature.view": false,
    "invoices.feature.create": false,
    "invoices.feature.edit": false,
    "invoices.feature.delete": false,
    "invoices.feature.editDatePaid": false,
    "invoices.feature.send": false,
    "invoices.feature.import": false,
    "invoices.feature.export": false,
    "invoices.feature.bulkActions": false,
    "billing.module.visible": false,
    "billing.feature.view": false,
    "billing.feature.edit": false,
    "billing.feature.viewPayables": false,
    "billing.feature.viewReceivables": false,
    "billing.feature.viewCommissions": false,
    "billing.feature.logExpense": false,
    "billing.feature.payViaPaypal": false,
    "billing.feature.overrideCommission": false,
    "schedule.module.visible": true,
    "schedule.feature.view": true,
    "schedule.feature.edit": false,
    "booking.feature.advancedSettings": false,
    "approvals.module.visible": false,
    "approvals.feature.viewRequests": false,
    "approvals.feature.approve": false,
    "approvals.feature.convert": false,
    "communications.module.visible": true,
    "communications.feature.viewTemplates": true,
    "communications.feature.editTemplates": true,
    "communications.feature.sendEmails": true,
    "communications.feature.viewAutomations": true,
    "communications.feature.editAutomations": true,
    "communications.feature.viewDripCampaigns": true,
    "communications.feature.editDripCampaigns": true,
    "communications.feature.viewSmsCampaigns": true,
    "communications.feature.editSmsCampaigns": true,
    "communications.feature.sendSms": true,
    "communications.feature.viewMasterDripCampaigns": false,
    "communications.feature.viewMasterTemplates": false,
    "calendarHub.module.visible": false,
    "calendarHub.feature.viewTimeline": false,
    "calendarHub.feature.viewDashboard": false,
    "calendarHub.feature.viewGrid": false,
    "calendarHub.feature.viewOrgTotals": false,
    "calendarHub.feature.editTasks": false,
    "calendarHub.feature.editTimeBlocks": true,
    "calendarHub.feature.editGoals": false,
    "calendarHub.feature.editFocus": false,
    "calendarHub.feature.editRegions": false,
    "leadGen.module.visible": true,
    "leadGen.feature.search": true,
    "leadGen.feature.import": true,
    "leadGen.feature.clear": true,
    "reports.module.visible": true,
    "reports.feature.viewRevenue": false,
    "reports.feature.viewBookings": true,
    "reports.feature.exportReports": true,
    "myProfile.module.visible": false,
    "myProfile.feature.edit": false,
    "myEvents.module.visible": false,
    "myEvents.feature.view": false,
    "adminPanel.module.visible": false,
    "adminPanel.feature.manageUsers": false,
    "adminPanel.feature.accessRequests": false,
    "adminPanel.feature.workflowSettings": false,
    "adminPanel.feature.managePermissions": false,
    "adminPanel.feature.manageCommissions": false,
    "adminPanel.feature.lateFeeSettings": false,
    "adminPanel.feature.manageTalent": false,
    "adminPanel.feature.manageClientTypes": false,
    "adminPanel.feature.brandingSettings": false,
    "adminPanel.feature.paymentPortal": false,
    "adminPanel.feature.invoiceSettings": false,
    "adminPanel.feature.profileSettings": false,
    "adminPanel.feature.approvalSettings": false,
    "adminPanel.feature.teamActivity": false,
    "adminPanel.feature.communicationsSettings": false,
    "adminPanel.feature.signatureBlock": false,
    "adminPanel.feature.workspaceIcon": false,
    "adminPanel.feature.talentIntakeForms": false,
    "adminPanel.feature.regionForms": false,
    "adminPanel.feature.bulkOperations": false,
    "adminPanel.feature.activityTracker": false,
    "billing.feature.billingAudit": false,
    "forms.module.visible": false,
    "forms.feature.queueView": false,
    "forms.feature.send": false,
    "forms.feature.uploadCompleted": false,
    "forms.feature.manageTemplates": false,
    "forms.feature.manageDocusign": false,
    "profiles.module.visible": false,
    "profiles.feature.view": false,
    "platform.module.visible": false,
    "platform.feature.managePlatformPermissions": false,
    "platform.feature.viewAllWorkspaces": false,
    "marketing.module.visible": true,
      "marketing.feature.viewContacts": true,
      "marketing.feature.editContacts": true,
      "marketing.feature.importContacts": true,
      "marketing.feature.viewNewsletter": true,
      "marketing.feature.editNewsletter": true,
      "marketing.feature.sendNewsletter": true,
      "marketing.feature.viewDistribution": true,
      "marketing.feature.launchDistribution": true,
      "marketing.feature.viewAds": true,
      "marketing.feature.manageAds": true,
    "socialMedia.module.visible": true,
    "socialMedia.feature.viewCalendar": true,
    "socialMedia.feature.viewMediaLibrary": true,
    "socialMedia.feature.uploadMedia": true,
    "socialMedia.feature.deleteMedia": true,
    "socialMedia.feature.createDraft": true,
    "socialMedia.feature.editDraft": true,
    "socialMedia.feature.deletePost": true,
    "socialMedia.feature.schedulePost": true,
    "socialMedia.feature.publishPost": true,
    "socialMedia.feature.approvePost": true,
    "socialMedia.feature.viewAnalytics": true,
    "socialMedia.feature.manageConnections": true,
    "socialMedia.feature.manageSettings": true,
    "socialMedia.feature.manageCollaborators": true,
    "adminPanel.feature.socialMediaSettings": false,
    "knowledge.module.visible": true,
    "knowledge.feature.manage": false,
    "platform.feature.manageWorkspaces": false,
  },
};

export function getModulesFromRegistry(): string[] {
  const modules = new Set<string>();
  for (const perm of PERMISSION_REGISTRY) {
    modules.add(perm.module);
  }
  return Array.from(modules);
}

export function getPermissionsByModule(module: string): PermissionDefinitionEntry[] {
  return PERMISSION_REGISTRY.filter(p => p.module === module).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getModuleVisibilityKey(module: string): string {
  return `${module}.module.visible`;
}
